import { ref } from 'vue'
import { defineStore } from 'pinia'

const DISMISSED_KEY = 'install_prompt_dismissed'
const SNOOZE_KEY = 'install_prompt_snooze_until'
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000 // 7-day "remind me later" window

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null // blocked storage (e.g. private-mode Safari)
  }
}
function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* degrade silently when storage is blocked */
  }
}
function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

export const useInstallPromptStore = defineStore('installPrompt', () => {
  const isOpen = ref(false)
  const hasAutoTriggered = ref(false)

  function isPermanentlyDismissed(): boolean {
    return readStorage(DISMISSED_KEY) === 'true'
  }
  function snoozeUntil(): number {
    const raw = readStorage(SNOOZE_KEY)
    const parsed = raw ? Number.parseInt(raw, 10) : 0
    return Number.isFinite(parsed) ? parsed : 0
  }
  function shouldShow(now: number): boolean {
    return !isPermanentlyDismissed() && now >= snoozeUntil()
  }

  // Fires at most once per app session; caller passes current install state.
  function autoTrigger(isInstalled: boolean, now: number = Date.now()): void {
    if (hasAutoTriggered.value) return
    hasAutoTriggered.value = true
    if (isInstalled) return
    if (shouldShow(now)) isOpen.value = true
  }

  function remindLater(now: number = Date.now()): void {
    writeStorage(SNOOZE_KEY, String(now + SNOOZE_MS))
    isOpen.value = false
  }

  function dontRemindAgain(): void {
    writeStorage(DISMISSED_KEY, 'true')
    removeStorage(SNOOZE_KEY)
    isOpen.value = false
  }

  // Called when the PWA is installed (via our button or any other path).
  function installed(): void {
    writeStorage(DISMISSED_KEY, 'true')
    removeStorage(SNOOZE_KEY)
    isOpen.value = false
  }

  function close(): void {
    isOpen.value = false
  }

  return { isOpen, hasAutoTriggered, autoTrigger, remindLater, dontRemindAgain, installed, close }
})
